import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { postApiAsync } from "../../api/api";
import '../../assets/post.css';
import '../../assets/common.css';
import '../../assets/markdown.css';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/atom-one-dark.css";
import { Helmet } from "react-helmet";

// Mock 文章元資料
import articlesMetadata from '../../mockData/articles.json';

// 根據 ID 獲取 Mock 文章
const getMockArticle = async (id) => {
    try {
        // 動態導入對應的文章檔案
        let articleModule;
        let title;
        
        if (articlesMetadata[id]) {
            // 有對應的文章
            const articleInfo = articlesMetadata[id];
            title = articleInfo.title;
            
            switch(id) {
                case '1':
                    articleModule = await import('../../mockData/article-1.md');
                    break;
                case '2':
                    articleModule = await import('../../mockData/article-2.md');
                    break;
                case '3':
                    articleModule = await import('../../mockData/article-3.md');
                    break;
                case '4':
                    articleModule = await import('../../mockData/article-4.md');
                    break;
                case '5':
                    articleModule = await import('../../mockData/article-5.md');
                    break;
                default:
                    throw new Error('Unknown article ID');
            }
        } else {
            // 沒有對應的文章，使用映射邏輯
            const articleNumber = parseInt(id) || 1;
            const availableIds = Object.keys(articlesMetadata);
            const selectedId = availableIds[articleNumber % availableIds.length];
            const articleInfo = articlesMetadata[selectedId];
            title = `${articleInfo.title} (ID: ${id})`;
            
            switch(selectedId) {
                case '1':
                    articleModule = await import('../../mockData/article-1.md');
                    break;
                case '2':
                    articleModule = await import('../../mockData/article-2.md');
                    break;
                case '3':
                    articleModule = await import('../../mockData/article-3.md');
                    break;
                case '4':
                    articleModule = await import('../../mockData/article-4.md');
                    break;
                case '5':
                    articleModule = await import('../../mockData/article-5.md');
                    break;
                default:
                    articleModule = await import('../../mockData/article-1.md');
            }
        }
        
        // 獲取文章內容
        const content = await fetch(articleModule.default).then(res => res.text());
        
        return {
            title: title,
            content: content
        };
        
    } catch (error) {
        console.error('Error loading mock article:', error);
        
        // 降級方案：使用內嵌的簡單內容
        const fallbackContent = `# Mock 文章 (ID: ${id})

這是一個簡單的 Mock 文章範例。

## 功能特點

- **Markdown 渲染**：支援完整的 Markdown 語法
- **語法高亮**：程式碼區塊語法高亮
- **響應式設計**：適配各種螢幕尺寸

## 程式碼範例

\`\`\`javascript
console.log('Hello, Mock Article!');
\`\`\`

> 這是一個引用區塊

---

*檔案讀取失敗，使用降級內容*`;

        return {
            title: `Mock 文章 (ID: ${id})`,
            content: fallbackContent
        };
    }
};

const PostIdea = ({id}) => {
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        systemType: '',
        metaDescription: '',
        metaKeywords: '',
        canonicalUrl: '',
        robotsTag: '',
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        ogType: '',
        ogSiteName: '',
        twitterCard: '',
        twitterTitle: '',
        twitterDescription: '',
        twitterImage: '',
        author: '',
        publishedTime: '',
        modifiedTime: '',
        articleSection: '',
        articleTags: '',
        schemaMarkup: '',
        seoSlug: '',
        focusKeyword: ''
    });
    const [readingProgress, setReadingProgress] = useState(0);
    const [useMock, setUseMock] = useState(false); // Mock 開關狀態 - 手動修改此處來控制

    // 初始和分頁加載數據
    useEffect(() => {
        // 如果啟用 Mock 模式，根據傳入的 ID 獲取對應的 Mock 數據
        if (useMock) {
            getMockArticle(id).then(mockData => {
                setFormData(mockData);
            });
            return;
        }

        // 定義異步函數以調用 API
        const fetchData = async () => {
            try {
                const response = await postApiAsync(id);
                console.log("API Response:", response);
                setFormData(response);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData(); // 執行異步函數
    }, [id, useMock]);

    // 閱讀進度追蹤
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = (scrollTop / docHeight) * 100;
            setReadingProgress(Math.min(scrollPercent, 100));
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 生成結構化數據
    const generateStructuredData = () => {
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": formData.title,
            "description": formData.metaDescription,
            "image": formData.ogImage,
            "author": {
                "@type": "Person",
                "name": formData.author
            },
            "publisher": {
                "@type": "Organization",
                "name": formData.ogSiteName,
                "logo": {
                    "@type": "ImageObject",
                    "url": formData.ogImage
                }
            },
            "datePublished": formData.publishedTime,
            "dateModified": formData.modifiedTime,
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": formData.canonicalUrl
            },
            "keywords": formData.metaKeywords,
            "articleSection": formData.articleSection,
            "articleBody": formData.content
        };

        return JSON.stringify(structuredData);
    };

    // 自定義 Markdown 渲染器
    const customRenderers = {
        img: ({ src, alt }) => (
            <img
                src={src}
                alt={alt}
                style={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
                onClick={() => window.open(src, "_blank")}
                onMouseEnter={(e) => {
                    e.target.style.transform = "scale(1.02)";
                    e.target.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                }}
            />
        ),
        code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
                <pre className="code-block">
                    <code className={className} {...props}>
                        {children}
                    </code>
                </pre>
            ) : (
                <code className="inline-code" {...props}>
                    {children}
                </code>
            );
        },
        blockquote: ({ children }) => (
            <blockquote className="elegant-quote">
                {children}
            </blockquote>
        ),
        h1: ({ children }) => (
            <h1 className="markdown-h1">{children}</h1>
        ),
        h2: ({ children }) => (
            <h2 className="markdown-h2">{children}</h2>
        ),
        h3: ({ children }) => (
            <h3 className="markdown-h3">{children}</h3>
        ),
        p: ({ children }) => (
            <p className="markdown-paragraph">{children}</p>
        ),
        ul: ({ children }) => (
            <ul className="markdown-list">{children}</ul>
        ),
        ol: ({ children }) => (
            <ol className="markdown-list">{children}</ol>
        ),
        li: ({ children }) => (
            <li className="markdown-list-item">{children}</li>
        ),
        table: ({ children }) => (
            <div className="table-wrapper">
                <table className="markdown-table">{children}</table>
            </div>
        ),
    };

    return (
        <div>
            <Helmet>
                {/* 基本 SEO 標籤 */}
                <title>{formData.title || '載入中...'}</title>
                <meta name="description" content={formData.metaDescription} />
                <meta name="keywords" content={formData.metaKeywords} />
                <meta name="robots" content={formData.robotsTag || 'index, follow'} />
                <link rel="canonical" href={formData.canonicalUrl} />
                
                {/* Open Graph 標籤 */}
                <meta property="og:title" content={formData.ogTitle || formData.title} />
                <meta property="og:description" content={formData.ogDescription || formData.metaDescription} />
                <meta property="og:type" content={formData.ogType || 'article'} />
                <meta property="og:image" content={formData.ogImage} />
                <meta property="og:site_name" content={formData.ogSiteName} />
                <meta property="og:url" content={formData.canonicalUrl} />
                
                {/* Twitter Cards 標籤 */}
                <meta name="twitter:card" content={formData.twitterCard || 'summary_large_image'} />
                <meta name="twitter:title" content={formData.twitterTitle || formData.title} />
                <meta name="twitter:description" content={formData.twitterDescription || formData.metaDescription} />
                <meta name="twitter:image" content={formData.twitterImage || formData.ogImage} />
                
                {/* 文章特定標籤 */}
                <meta name="author" content={formData.author} />
                <meta property="article:published_time" content={formData.publishedTime} />
                <meta property="article:modified_time" content={formData.modifiedTime} />
                <meta property="article:section" content={formData.articleSection} />
                {formData.articleTags && formData.articleTags.split(',').map((tag, index) => (
                    <meta key={index} property="article:tag" content={tag.trim()} />
                ))}
                
                {/* 結構化數據 */}
                <script type="application/ld+json">
                    {formData.schemaMarkup || generateStructuredData()}
                </script>
            </Helmet>
            
            {/* 閱讀進度指示器 */}
            <div 
                className="reading-progress" 
                style={{ transform: `scaleX(${readingProgress / 100})` }}
            />
            
            <div className="markdown-container">
                {/* 文章標題 */}
                <h1 className="post-title-elegant">
                    {formData.title || '載入中...'}
                </h1>
                
                {/* 文章元數據 */}
                {/* <div className="post-meta">
                    {formData.author && <span className="post-author">作者: {formData.author}</span>}
                    {formData.publishedTime && <span className="post-date">發布時間: {new Date(formData.publishedTime).toLocaleDateString()}</span>}
                    {formData.articleSection && <span className="post-section">分類: {formData.articleSection}</span>}
                    {formData.articleTags && (
                        <div className="post-tags">
                            標籤: {formData.articleTags.split(',').map((tag, index) => (
                                <span key={index} className="tag">{tag.trim()}</span>
                            ))}
                        </div>
                    )}
                </div> */}
                
                {/* 文章內容 */}
                {formData.content && (
                    <ReactMarkdown
                        components={customRenderers}
                        children={formData.content}
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeHighlight]}
                    />
                )}
                
                {/* 載入狀態 */}
                {!formData.content && (
                    <div className="loading-container">
                        <div className="loading-spinner" />
                        <p>正在載入文章內容...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PostIdea;